import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from "firebase/auth";
import { collection, addDoc, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { Box } from '@mui/material';
import { auth, db } from './firebase';
import Auth from './Auth';
import Home from './Home';
import Publicar from './Publicar';
import Comentarios from './Comentarios';
import Buscar from './Buscar';
import Perfil from './Perfil';
import Mensajes from './Mensajes';
import Notificaciones from './Notificaciones';
import PerfilUsuario from './PerfilUsuario';
import Promocionar from './Promocionar';

// Ubicación aproximada (ciudad) por IP para el historial de accesos; si falla, se guarda sin ubicación
const fetchApproxLocation = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch('https://ipwho.is/?fields=success,city,region,country,latitude,longitude', {
      signal: controller.signal,
    });
    const data = await res.json();
    if (!data?.success || typeof data.latitude !== 'number' || typeof data.longitude !== 'number') return null;
    return {
      city: data.city || '',
      region: data.region || '',
      country: data.country || '',
      lat: data.latitude,
      lng: data.longitude,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        localStorage.setItem('user', JSON.stringify({ uid: currentUser.uid }));
        // Se registra un acceso por sesión de pestaña y además en cada inicio de sesión nuevo
        const loggedKey = `accessLogged_${currentUser.uid}`;
        const signInKey = `lastSignInLogged_${currentUser.uid}`;
        const lastSignIn = currentUser.metadata?.lastSignInTime || '';
        let alreadyLogged = false;
        try {
          alreadyLogged = !!sessionStorage.getItem(loggedKey) && localStorage.getItem(signInKey) === lastSignIn;
          sessionStorage.setItem(loggedKey, '1');
          localStorage.setItem(signInKey, lastSignIn);
        } catch {}
        if (!alreadyLogged) {
          fetchApproxLocation().then((location) =>
            addDoc(collection(db, 'users', currentUser.uid, 'accessLogs'), {
              userAgent: navigator.userAgent,
              timestamp: serverTimestamp(),
              ...(location ? { location } : {}),
            })
          ).catch(() => {});
        }
      } else {
        localStorage.removeItem('user');
      }
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Quien entra con Google por primera vez no tiene perfil en Firestore: hasta que lo
  // complete se le muestra el formulario de alta. Solo se consulta para cuentas de Google,
  // así el arranque de las cuentas con email no espera esta lectura.
  const isGoogleUser = !!user?.providerData?.some((p) => p.providerId === 'google.com');
  const [profileStatus, setProfileStatus] = useState('loading');
  useEffect(() => {
    if (!user?.uid || !isGoogleUser) return undefined;
    setProfileStatus('loading');
    return onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => setProfileStatus(snap.exists() ? 'exists' : 'missing'),
      () => setProfileStatus('exists')
    );
  }, [user?.uid, isGoogleUser]);

  const waitingProfile = isGoogleUser && profileStatus === 'loading';
  if (loading || waitingProfile) {
    return (
      <Box sx={{ bgcolor: '#000', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src="/logo192.png" alt="Cargando..." style={{ height: 50 }} />
      </Box>
    );
  }

  if (isGoogleUser && profileStatus === 'missing') {
    return <Auth completeProfileFor={user} />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/mensajes" element={user ? <Mensajes /> : <Navigate to="/auth" />} />
        <Route path="/notificaciones" element={user ? <Notificaciones /> : <Navigate to="/auth" />} />
        <Route path="/publicar" element={user ? <Publicar /> : <Navigate to="/auth" />} />
        <Route path="/comentarios/:postId" element={user ? <Comentarios /> : <Navigate to="/auth" />} />
        <Route path="/buscar" element={user ? <Buscar /> : <Navigate to="/auth" />} />
        <Route path="/perfilusuario/:id" element={user ? <PerfilUsuario /> : <Navigate to="/auth" />} />
        <Route path="/perfil" element={user ? <Perfil /> : <Navigate to="/auth" />} />
        <Route path="/promocionar" element={user ? <Promocionar /> : <Navigate to="/auth" />} />
        <Route path="/" element={user ? <Home /> : <Navigate to="/auth" />} />
        <Route path="/auth" element={!user ? <Auth /> : <Navigate to="/" />} />
        <Route path="*" element={user ? <Navigate to="/" /> : <Navigate to="/auth" />} />
      </Routes>
    </Router>
  );
}

export default App;